import { ReactElement, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import { Alert, Box, Button, Chip, Collapse, Divider, Paper, Typography } from '@mui/material';
import { IOrganisationTeamItemSummary, IOrganisationTeamsResponse } from '../../types/@organisation';
import { ROUTES } from '../../helpers/constans';

interface IProps {
  teams: IOrganisationTeamsResponse;
}

type TExpandedState = Record<string, boolean>;

const ItemListPanel = ({
  title,
  items,
  expanded,
  onToggle,
}: {
  title: string;
  items: IOrganisationTeamItemSummary[];
  expanded: boolean;
  onToggle: () => void;
}): ReactElement => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        p: 2,
        borderRadius: '12px',
        border: '1px solid #e3e8ef',
        background: '#fafbfd',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#17324d' }}>
          {title}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          onClick={onToggle}
          sx={{ textTransform: 'none', borderRadius: '10px' }}
        >
          {expanded ? 'Hide items' : `Show items (${items.length})`}
        </Button>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5 }}>
          {items.length ? (
            items.map((item) => (
              <Box
                key={item.qdosItemId}
                onClick={() =>
                  navigate(generatePath(ROUTES.thignsQdosMediaShow, { id: item.qdosItemId }))
                }
                sx={{
                  p: 1.5,
                  borderRadius: '10px',
                  border: '1px solid #dbe5ef',
                  background: '#fff',
                  cursor: 'pointer',
                  '&:hover': {
                    background: '#f4f8fc',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                    <Typography sx={{ fontWeight: 700, color: '#17324d' }}>
                      {item.ownerName}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#6b7c8f' }}>
                      QDOS item: {item.qdosItemId}
                    </Typography>
                    {item.alternativeId && (
                      <Typography sx={{ fontSize: '13px', color: '#6b7c8f' }}>
                        Ref: {item.alternativeId}
                      </Typography>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip size="small" label={`Posts: ${item.postsTotal}`} />
                    <Chip size="small" label={`Pics: ${item.picsTotal}`} />
                    <Chip size="small" label={`Clips: ${item.clipsTotal}`} />
                  </Box>
                </Box>
              </Box>
            ))
          ) : (
            <Alert severity="info">No linked items found.</Alert>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

const OrganisationTeamsPanel = ({ teams }: IProps): ReactElement => {
  const [expandedPanels, setExpandedPanels] = useState<TExpandedState>({});

  const togglePanel = (key: string) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const mismatchCount = useMemo(() => teams.summary.mismatchCount || 0, [teams.summary.mismatchCount]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
          Teams
        </Typography>
        <Chip
          label={teams.source === 'qdos' ? 'Source: QDOS' : 'Source unavailable'}
          color={teams.source === 'qdos' ? 'primary' : 'default'}
        />
      </Box>

      {teams.warnings?.length > 0 && (
        <Alert severity="warning">
          {teams.warnings.map((warning, index) => (
            <Box key={index}>{warning}</Box>
          ))}
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
        }}
      >
        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Teams</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.teamCount}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Player shirts</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.totalPlayerShirts}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Fan shirts</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {teams.summary.totalFanShirts}
          </Typography>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: '14px' }} elevation={0} variant="outlined">
          <Typography sx={{ fontSize: '13px', color: '#6b7c8f', mb: 1 }}>Mismatches</Typography>
          <Typography sx={{ fontSize: '24px', fontWeight: 700, color: '#17324d' }}>
            {mismatchCount}
          </Typography>
        </Paper>
      </Box>

      {teams.teams.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {teams.teams.map((team) => {
            const healthy = team.playersConsistent && team.fansConsistent;
            const playersKey = `${team.teamId}-players`;
            const fansKey = `${team.teamId}-fans`;

            return (
              <Paper
                key={team.teamId}
                sx={{
                  p: 3,
                  borderRadius: '16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
                elevation={0}
                variant="outlined"
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: 1,
                  }}
                >
                  <Box>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#17324d' }}>
                      {team.name}
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: '#6b7c8f' }}>
                      Team ID: {team.teamId}
                    </Typography>
                  </Box>

                  <Chip
                    label={healthy ? 'Healthy' : 'Needs attention'}
                    color={healthy ? 'success' : 'warning'}
                  />
                </Box>

                <Divider />

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <ItemListPanel
                    title={`Players · QDOS ${team.players.qCount} · XDOS ${team.players.xCount}`}
                    items={team.players.items}
                    expanded={!!expandedPanels[playersKey]}
                    onToggle={() => togglePanel(playersKey)}
                  />

                  <ItemListPanel
                    title={`Fans · QDOS ${team.fans.qCount} · XDOS ${team.fans.xCount}`}
                    items={team.fans.items}
                    expanded={!!expandedPanels[fansKey]}
                    onToggle={() => togglePanel(fansKey)}
                  />
                </Box>

                {team.warnings.length > 0 && (
                  <Alert severity="warning">
                    {team.warnings.map((warning, index) => (
                      <Box key={index}>{warning}</Box>
                    ))}
                  </Alert>
                )}
              </Paper>
            );
          })}
        </Box>
      ) : (
        <Alert severity="info">No team data is currently available for this organisation.</Alert>
      )}
    </Box>
  );
};

export default OrganisationTeamsPanel;